import { useState, useCallback, useRef } from 'react';
import { Card, Button, Badge, Alert, Form } from 'react-bootstrap';
import RouteEnergyChart from './RouteEnergyChart';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FuelStrategyResponse,
  ProductAllocationResponse,
  ProductInfo,
  ProductType,
  RouteAnalysisResponse,
  StrategyOptions,
} from '../../models';
import FuelSummaryCard from './FuelSummaryCard';
import PlannerNavigation from './PlannerNavigation';

interface Props {
  strategy: FuelStrategyResponse;
  route: RouteAnalysisResponse;
  strategyOptions: StrategyOptions;
  onBack: () => void;
  onNext?: () => void;
  bidonMl: number;
  formProducts: ProductInfo[];
}

const PRODUCT_ICONS: Record<ProductType, string> = {
  GEL: '💊',
  BAR: '🍫',
  DRINK: '🥤',
  CHEW: '🍬',
  GUMMY: '🐻',
  FOOD: '🍌',
};

function formatDuration(iso: string): string {
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return iso;
  const h = parseInt(match[1] ?? '0');
  const m = parseInt(match[2] ?? '0');
  return `${h}:${String(m).padStart(2, '0')}`;
}

function toReadableFraction(value: number): string {
  const DENOMS = [1, 2, 3, 4, 5, 6, 8];
  let bestNum = 1, bestDen = 1, bestDiff = Math.abs(value - 1);
  for (const den of DENOMS) {
    const num = Math.round(value * den);
    if (num <= 0) continue;
    const diff = Math.abs(value - num / den);
    if (diff < bestDiff) { bestNum = num; bestDen = den; bestDiff = diff; }
  }
  return bestNum === bestDen ? '1' : `${bestNum}/${bestDen}`;
}

function formatProduct(p: { name: string; quantity: number }, formProducts: ProductInfo[], bidonMl: number): string {
  const fp = formProducts.find(x => x.name === p.name);
  const icon = fp ? PRODUCT_ICONS[fp.type] : '';
  if (fp?.type === 'DRINK') {
    const fraction = toReadableFraction(p.quantity);
    const ml = Math.round(p.quantity * bidonMl);
    return `${icon} ${p.name} ${fraction} bottle (${ml}ml)`;
  }
  return `${icon} ${p.name} ×${parseFloat(p.quantity.toFixed(2))}`;
}

interface AssignmentItem {
  id: number;
  products: ProductAllocationResponse[];
  totalCarbs: number;
  totalKcal: number;
  burnedKcal: number;
}

const ROW_H = 58;
const ROW_MB = 4;
const DRINK_FRACTIONS = [1 / 5, 1 / 4, 1 / 3, 1 / 2, 1.0];

function SortableAssignmentCard({ item, formProducts, bidonMl, onDelete, onSave, autoEdit }: {
  item: AssignmentItem;
  formProducts: ProductInfo[];
  bidonMl: number;
  onDelete: (id: number) => void;
  onSave: (id: number, products: ProductAllocationResponse[], totalCarbs: number) => void;
  autoEdit?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const [isEditing, setIsEditing] = useState(autoEdit ?? false);
  const [editProducts, setEditProducts] = useState<{ name: string; quantity: number }[]>(() => {
    if (item.products.length > 0) return item.products.map(p => ({ name: p.name, quantity: p.quantity }));
    const first = formProducts[0];
    if (!first) return [];
    return [{ name: first.name, quantity: first.type === 'DRINK' ? 0.5 : 1 }];
  });

  const startEdit = () => {
    setEditProducts(
      item.products.length > 0
        ? item.products.map(p => ({ name: p.name, quantity: p.quantity }))
        : formProducts[0] ? [{ name: formProducts[0].name, quantity: formProducts[0].type === 'DRINK' ? 0.5 : 1 }] : []
    );
    setIsEditing(true);
  };

  const handleNameChange = (idx: number, name: string) => {
    const fp = formProducts.find(p => p.name === name);
    const defaultQty = fp?.type === 'DRINK' ? 0.5 : 1;
    setEditProducts(prev => prev.map((ep, i) => i === idx ? { name, quantity: defaultQty } : ep));
  };

  const handleQtyChange = (idx: number, quantity: number) => {
    setEditProducts(prev => prev.map((ep, i) => i === idx ? { ...ep, quantity } : ep));
  };

  const handleRemove = (idx: number) => {
    setEditProducts(prev => prev.filter((_, i) => i !== idx));
  };


  const saveEdit = () => {
    const newProducts: ProductAllocationResponse[] = editProducts.map(ep => {
      const fp = formProducts.find(p => p.name === ep.name);
      const carbs = Math.round(ep.quantity * (fp?.carbs ?? 0));
      return { name: ep.name, quantity: ep.quantity, carbs };
    });
    onSave(item.id, newProducts, newProducts.reduce((s, p) => s + p.carbs, 0));
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className="row-margin-bottom">
        <Card className="card-dark-compact">
          <Card.Body className="px-3 py-2">
            {editProducts.map((ep, idx) => {
              const fp = formProducts.find(p => p.name === ep.name);
              const isDrink = fp?.type === 'DRINK';
              return (
                <div key={idx} className="d-flex align-items-center gap-2 mb-1">
                  <Form.Select size="sm" value={ep.name} onChange={e => handleNameChange(idx, e.target.value)} className="form-input-flex">
                    {formProducts.map(p => (
                      <option key={p.name} value={p.name}>{PRODUCT_ICONS[p.type]} {p.name}</option>
                    ))}
                  </Form.Select>
                  {isDrink ? (
                    <Form.Select size="sm" value={ep.quantity} onChange={e => handleQtyChange(idx, parseFloat(e.target.value))} className="form-select-fraction">
                      {DRINK_FRACTIONS.map(f => (
                        <option key={f} value={f}>{toReadableFraction(f)}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control size="sm" type="number" min={1} value={ep.quantity} onChange={e => handleQtyChange(idx, parseInt(e.target.value) || 1)} className="form-input-qty" />
                  )}
                  <Button size="sm" variant="outline-danger" onClick={() => handleRemove(idx)}>✕</Button>
                </div>
              );
            })}
            <div className="d-flex gap-2 mt-2">
              <Button size="sm" variant="primary" onClick={saveEdit}>Zapisz</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setIsEditing(false)}>Anuluj</Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : 1,
        height: ROW_H,
        marginBottom: ROW_MB,
        position: 'relative',
        zIndex: isDragging ? 10 : undefined,
      }}
    >
      <Card className="card-dark-compact h-100">
        <Card.Body className="py-0 px-3 d-flex align-items-center gap-3 h-100">
          <span
            {...attributes}
            {...listeners}
            className="drag-handle"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            ⠿
          </span>
          <span className="flex-grow-1 text-light">
            {item.products.map(p => formatProduct(p, formProducts, bidonMl)).join(', ')}
          </span>
          <Badge bg="success">{item.totalCarbs} g</Badge>
          <Button size="sm" variant="outline-secondary" onClick={startEdit}>✎</Button>
          <Button size="sm" variant="outline-danger" onClick={() => onDelete(item.id)}>✕</Button>
        </Card.Body>
      </Card>
    </div>
  );
}


export default function FuelStrategyResults({
  strategy, route, strategyOptions, onBack, onNext, bidonMl, formProducts,
}: Props) {
  const filledPoints = strategy.points.filter(p => p.totalCarbs > 0);
  const [slots, setSlots] = useState(filledPoints.map(p => ({ km: p.kilometerMark, time: p.estimatedTime })));

  const [assignments, setAssignments] = useState<AssignmentItem[]>(
    filledPoints.map((p, i) => ({
      id: i,
      products: p.products,
      totalCarbs: p.totalCarbs,
      totalKcal: p.totalKcal,
      burnedKcal: p.burnedKcal,
    }))
  );
  const [pendingEditId, setPendingEditId] = useState<number | null>(null);
  const nextIdRef = useRef(filledPoints.length);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      setAssignments(prev => {
        const from = prev.findIndex(a => a.id === active.id);
        const to = prev.findIndex(a => a.id === over.id);
        return arrayMove(prev, from, to);
      });
    }
  };

  const handleDelete = (id: number) => {
    setAssignments(prev => {
      const idx = prev.findIndex(a => a.id === id);
      setSlots(s => s.filter((_, i) => i !== idx));
      return prev.filter(a => a.id !== id);
    });
  };

  const handleSave = (id: number, products: ProductAllocationResponse[], totalCarbs: number) => {
    setPendingEditId(null);
    setAssignments(prev => prev.map(a => a.id === id ? { ...a, products, totalCarbs } : a));
  };

  const handleChartClick = useCallback((data: any) => {
    if (data?.activeLabel === undefined) return;
    const km = parseFloat(Number(data.activeLabel).toFixed(1));
    if (isNaN(km) || km <= 0 || km >= route.totalDistanceKm) return;
    if (slots.some(s => Math.abs(s.km - km) < 0.5)) return;
    const newId = nextIdRef.current++;
    const insertIdx = slots.findIndex(s => s.km > km);
    const idx = insertIdx === -1 ? slots.length : insertIdx;
    setSlots(prev => { const next = [...prev]; next.splice(idx, 0, { km, time: 'PT0S' }); return next; });
    setAssignments(prev => { const next = [...prev]; next.splice(idx, 0, { id: newId, products: [], totalCarbs: 0, totalKcal: 0, burnedKcal: 0 }); return next; });
    setPendingEditId(newId);
  }, [slots, route.totalDistanceKm]);

  const visibleWarnings = strategy.warnings.filter(w => {
    const lw = w.toLowerCase();
    return !lw.startsWith('bonk') && !lw.includes('deficit');
  });

  return (
    <div className="d-flex flex-column gap-3">
      <FuelSummaryCard
        totalCarbs={assignments.reduce((s, a) => s + a.totalCarbs, 0)}
        totalConsumedKcal={assignments.reduce((s, a) => s + a.totalCarbs, 0) * 4}
        totalBurnedKcal={strategy.points.reduce((s, p) => s + p.burnedKcal, 0)}
        totalDuration={strategy.totalDuration}
      />

      {visibleWarnings.length > 0 && (
        <div className="d-flex flex-column gap-2">
          {visibleWarnings.map((w, i) => (
            <Alert key={i} variant="warning" className="mb-0">{w}</Alert>
          ))}
        </div>
      )}

      <Card className="sticky-header">
        <Card.Header><Card.Title>Route</Card.Title></Card.Header>
        <Card.Body>
          <RouteEnergyChart
            route={route}
            strategyOptions={strategyOptions}
            fuelPoints={slots.map((slot, i) => ({ km: slot.km, carbs: assignments[i]?.totalCarbs ?? 0 }))}
            height={220}
            cursor="crosshair"
            onClick={handleChartClick}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><Card.Title className="mb-0">Fuel Points</Card.Title></Card.Header>
        <Card.Body>
          <div className="fuel-points-layout">
            <div className="drag-handle-spacer">
              {slots.map((slot, i) => (
                <div
                  key={i}
                  className="fuel-point-label-col row-margin-bottom"
                  style={{ height: ROW_H }}
                >
                  <div className="text-light fw-bold text-sm-compact">{slot.km.toFixed(1)} km</div>
                  <small className="text-secondary">{formatDuration(slot.time)}</small>
                </div>
              ))}
            </div>

            <div className="flex-grow-min-width">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={assignments.map(a => a.id)} strategy={verticalListSortingStrategy}>
                  {assignments.map(item => (
                    <SortableAssignmentCard
                      key={item.id}
                      item={item}
                      formProducts={formProducts}
                      bidonMl={bidonMl}
                      onDelete={handleDelete}
                      onSave={handleSave}
                      autoEdit={item.id === pendingEditId}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        </Card.Body>
      </Card>

      <PlannerNavigation
        onBack={onBack}
        onNext={onNext}
      />
    </div>
  );
}
